import React, { useState } from "react";
import styled from "@xstyled/styled-components";
import { FiInfo, FiLayers } from "react-icons/fi";
import { tokens } from "../../../utils/theme";
import { Panel, PanelHeader, PanelTitle } from "../../../components/SharedPageStyles";
import CustomSelect from "../../../components/CustomSelect";
import { useJournalData } from "../../../context/JournalContext";
import { useTradeStats } from "../../../hooks/useTradeStats";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.4)' : 'rgba(15, 118, 110, 0.3)'};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.6)' : 'rgba(15, 118, 110, 0.5)'};
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  min-height: 300px;
`;



const ChartSection = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'};
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const ChartTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  align-content: start;
`;

const MetricCard = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'};
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
`;

const MetricLabel = styled.div`
  font-size: 11px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.positive ? tokens(props.theme.palette.mode).greenAccent[500] : (props.negative ? tokens(props.theme.palette.mode).redAccent[500] : tokens(props.theme.palette.mode).grey[100])};
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  color: ${props => tokens(props.theme.palette.mode).grey[600]};
  z-index: 5;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[400]};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const CardActions = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  z-index: 5;
`;

const CardActionButton = styled.div`
  color: ${props => props.active ? tokens(props.theme.palette.mode).blueAccent[400] : tokens(props.theme.palette.mode).grey[600]};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[400]};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const DetailsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(35, 40, 59, 0.98)' : 'rgba(255, 255, 255, 0.98)'}; /* grey[500] approx */
  backdrop-filter: blur(4px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
  z-index: 10;
  cursor: pointer;
  animation: fadeIn 0.2s ease-in-out;
  text-align: center;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const OverlayText = styled.div`
  font-size: 11px;
  color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[100] : tokens(props.theme.palette.mode).grey[800]};
  line-height: 1.4;
  font-weight: 500;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: ${props => tokens(props.theme.palette.mode).grey[500]};
    text-transform: uppercase;
    white-space: nowrap;
  }

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${props => tokens(props.theme.palette.mode).grey[800]};
  }
`;

const MetricsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StatisticsTab = () => {
  const { filteredRows, userProfile, dateFilter, setDateFilter, filteredInitialBalance } = useJournalData();
  const [activeDetail, setActiveDetail] = useState(null);

  const stats = useTradeStats(filteredRows, filteredInitialBalance, userProfile, dateFilter);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(val);
  };

  const formatNumber = (val, decimals = 2) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(val);
  };

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Statistics</PanelTitle>
        <CustomSelect
          value={dateFilter}
          onChange={setDateFilter}
          options={[
            "All",
            "Today",
            "Last 7 days",
            "Last 30 days",
            "Last 3 months",
            "Last 6 months",
            "Last year"
          ]}
        />
      </PanelHeader>
      <StatsContainer>
        <TopSection>
          <ChartSection>
            <ChartTitle>Equity Curve</ChartTitle>
            <div style={{ height: '300px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.equityCurve}>
                  <defs>
                    <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={tokens('dark').greenAccent[500]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={tokens('dark').greenAccent[500]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke={tokens('dark').grey[500]}
                    tick={{ fill: tokens('dark').grey[500], fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke={tokens('dark').grey[500]}
                    tick={{ fill: tokens('dark').grey[500], fontSize: 10 }}
                    tickFormatter={(value) => `$${value} `}
                    tickLine={false}
                    axisLine={false}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: tokens('dark').primary[400], border: 'none', borderRadius: '4px' }}
                    itemStyle={{ color: tokens('dark').grey[100] }}
                    formatter={(value) => [`$${value.toFixed(2)} `, 'Balance']}
                    labelStyle={{ color: tokens('dark').grey[400] }}
                  />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke={tokens('dark').greenAccent[500]}
                    fillOpacity={1}
                    fill="url(#colorBalance)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartSection>

          <MetricsSection>
            <div>
              <SectionHeader>
                <span>Core</span>
              </SectionHeader>
              <MetricsGrid>
                {/* 1. Balance */}
                <MetricCard>
                  <MetricLabel>Balance</MetricLabel>
                  <MetricValue positive>{formatCurrency(stats.balance)}</MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('balance')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'balance' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Total funds currently realized in your account.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 2. ROI */}
                <MetricCard>
                  <MetricLabel>ROI (Return)</MetricLabel>
                  <MetricValue positive={stats.roi > 0} negative={stats.roi < 0}>
                    {stats.roi > 0 ? `+${formatNumber(stats.roi)}%` : `${formatNumber(stats.roi)}%`}
                  </MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('roi')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'roi' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Return On Investment: percentage gain or loss relative to your initial account balance.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 3. Max Drawdown */}
                <MetricCard>
                  <MetricLabel>Max Drawdown</MetricLabel>
                  <MetricValue negative={stats.maxDrawdown > 10}>
                    {formatNumber(stats.maxDrawdown)}%
                  </MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('maxDrawdown')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'maxDrawdown' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>The largest peak-to-valley percentage drop in account balance.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 4. Recovery Factor */}
                <MetricCard>
                  <MetricLabel>Recovery Factor</MetricLabel>
                  <MetricValue positive={stats.recoveryFactor >= 1.5} negative={stats.recoveryFactor < 1 && stats.recoveryFactor > 0}>
                    {stats.recoveryFactor === Infinity ? '∞' : formatNumber(stats.recoveryFactor)}
                  </MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('recoveryFactor')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'recoveryFactor' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Net profit divided by maximum drawdown.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 5. Expectancy (Realized) with Separate Description & Audit (Planned & Leakage) Buttons */}
                <MetricCard>
                  <MetricLabel>Expectancy</MetricLabel>
                  <MetricValue positive={stats.expectancyR > 0} negative={stats.expectancyR < 0}>
                    {stats.expectancyR > 0 ? `+${formatNumber(stats.expectancyR, 2)} R` : `${formatNumber(stats.expectancyR || 0, 2)} R`}
                  </MetricValue>
                  <CardActions>
                    <CardActionButton
                      active={activeDetail === 'expectancyAudit'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDetail(activeDetail === 'expectancyAudit' ? null : 'expectancyAudit');
                      }}
                      title="Planned & Leakage"
                    >
                      <FiLayers size={14} />
                    </CardActionButton>
                    <CardActionButton
                      active={activeDetail === 'expectancy'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDetail(activeDetail === 'expectancy' ? null : 'expectancy');
                      }}
                      title="Expectancy Description"
                    >
                      <FiInfo size={14} />
                    </CardActionButton>
                  </CardActions>
                  {activeDetail === 'expectancy' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>
                        Average expected return in R per trade over the long run.
                      </OverlayText>
                    </DetailsOverlay>
                  )}
                  {activeDetail === 'expectancyAudit' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText style={{ width: '100%', padding: '0 4px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: tokens('dark').grey[400], fontWeight: 500 }}>Planned:</span>
                            <span style={{ fontWeight: 600, color: (stats.expectancyRPlanned || 0) >= 0 ? '#38bdf8' : tokens('dark').grey[300] }}>
                              {(stats.expectancyRPlanned || 0) > 0 ? '+' : ''}{formatNumber(stats.expectancyRPlanned || 0, 2)} R
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: tokens('dark').grey[400], fontWeight: 500 }}>Leakage:</span>
                            {stats.leakageR !== null && stats.leakageR !== undefined ? (
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  color: stats.leakageR >= 0 ? '#10b981' : (stats.leakageR >= -0.15 ? '#f59e0b' : '#ef4444'),
                                  background: stats.leakageR >= 0 ? 'rgba(16, 185, 129, 0.15)' : (stats.leakageR >= -0.15 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)'),
                                  border: `1px solid ${stats.leakageR >= 0 ? 'rgba(16, 185, 129, 0.3)' : (stats.leakageR >= -0.15 ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)')}`
                                }}
                              >
                                {stats.leakageR > 0 ? '+' : ''}{formatNumber(stats.leakageR, 2)} R
                              </span>
                            ) : (
                              <span style={{ opacity: 0.6 }}>N/A</span>
                            )}
                          </div>
                        </div>
                      </OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 7. Profit Factor */}
                <MetricCard>
                  <MetricLabel>Profit Factor</MetricLabel>
                  <MetricValue positive={stats.profitFactor > 1.2} negative={stats.profitFactor < 1}>
                    {stats.profitFactor === Infinity ? '∞' : formatNumber(stats.profitFactor)}
                  </MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('profitFactor')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'profitFactor' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Ratio of gross profit to gross loss. &gt; 1.5 is considered solid and robust.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 8. Win Rate */}
                <MetricCard>
                  <MetricLabel>Win Rate</MetricLabel>
                  <MetricValue positive={stats.winRate >= 50} negative={stats.winRate < 50}>
                    {formatNumber(stats.winRate)}%
                  </MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('winRate')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'winRate' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Percentage of closed trades that resulted in a profit.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 9. Avg RRR */}
                <MetricCard>
                  <MetricLabel>Avg RRR</MetricLabel>
                  <MetricValue positive={stats.avgRRR >= 1} negative={stats.avgRRR < 1}>
                    {formatNumber(stats.avgRRR)}
                  </MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('avgRRR')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'avgRRR' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Average realized Risk:Reward Ratio across all completed trades.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 10. Total Trades */}
                <MetricCard>
                  <MetricLabel>Total Trades</MetricLabel>
                  <MetricValue>{stats.totalTrades}</MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('totalTrades')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'totalTrades' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Total number of closed trades analyzed in this period.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* 10. Avg Win / Loss */}
                <MetricCard>
                  <MetricLabel>Avg Win / Loss</MetricLabel>
                  <MetricValue>
                    <span style={{ color: tokens('dark').greenAccent[500] }}>{formatCurrency(stats.avgWin)}</span>
                    <span style={{ color: tokens('dark').grey[500], margin: '0 4px' }}>/</span>
                    <span style={{ color: tokens('dark').redAccent[500] }}>{formatCurrency(stats.avgLoss)}</span>
                  </MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('avgWinLoss')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'avgWinLoss' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Average profit per winning trade vs average loss per losing trade.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>
              </MetricsGrid>
            </div>

            <div>
              <SectionHeader>
                <span>Optional</span>
              </SectionHeader>
              <MetricsGrid>

                {/* Sharpe Ratio */}
                <MetricCard>
                  <MetricLabel>Sharpe Ratio</MetricLabel>
                  <MetricValue positive={stats.sharpeRatio >= 0.15} negative={stats.sharpeRatio < 0}>
                    {formatNumber(stats.sharpeRatio)}
                  </MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('sharpeRatio')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'sharpeRatio' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>
                        Risk-adjusted return measured as mean return over standard deviation.
                      </OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>

                {/* Total Lots */}
                <MetricCard>
                  <MetricLabel>Total Lots</MetricLabel>
                  <MetricValue>{formatNumber(stats.totalLots)}</MetricValue>
                  <InfoIconWrapper onClick={() => setActiveDetail('totalLots')}>
                    <FiInfo size={14} />
                  </InfoIconWrapper>
                  {activeDetail === 'totalLots' && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                      <OverlayText>Total volume traded in lots.</OverlayText>
                    </DetailsOverlay>
                  )}
                </MetricCard>
              </MetricsGrid>
            </div>
          </MetricsSection>
        </TopSection>
      </StatsContainer>
    </Panel >
  );
};

export default StatisticsTab;
